/**
 * 멀티플렉스 봉투 → 태스크 콜백 라우팅 (전송 계층 무의존 — 단위 테스트 가능).
 *
 * 탭 통합 스트림이 합쳐 보낸 SimStreamEnvelope를 kind별로 디코드해,
 * payload에 내장된 task_id로 각 태스크의 콜백에 분배한다.
 */
import { SCENARIO_LOG_CHANNEL, parseScenarioLogEvent, scenarioLogEventText, type ScenarioLogEvent } from '@/domain/scenario';
import {
  SimStreamEnvelope,
  SimStreamKind,
} from '@/stubs/stream/sim_task_stream_pb';
import { ScreenLogLine, SimState, Telemetry } from '@/stubs/stream/sim_stream_pb';
import { MinorEditSnapshot as MarsMinorEditSnapshot } from '@/stubs/mars/mars_task_stream_pb';
import type { SimStateSnapshot, TelemetrySnapshot } from '@/types/simulation';

/**
 * 프레임은 **모아서** 전달된다. 스트림이 한 프레임씩 콜백을 때리면 소비자는
 * 프레임 수만큼 스토어를 커밋하고, 커밋마다 차트 그리드 전체가 재렌더된다.
 * 라이브(수 초당 1건)에서는 무해하지만 이어보기 백로그는 수백~수천 프레임이
 * 한 번에 도착한다 — 2026-08-08 실측: 1,227프레임이 메인 스레드를 62초
 * 블록(단일 롱태스크 22.1초)했고, 그동안 화면은 빈 채였다. 화면 로그만
 * 이 버퍼링을 쓰고 있었고 나머지 종류는 빠져 있던 것이 원인이다.
 */
export interface PollingCallbacks {
  /** 수집할 화면 로그 채널 (생략=전체) — 시나리오 채널(`SCENARIO_LOG_CHANNEL`)은 이 필터와 무관하게 받는다 */
  screenLogChannels?: readonly string[];
  onScreenLogs?: (logs: string[]) => void;
  /** 시나리오 적용 사건(«scenario» 채널의 구조화된 줄) — 로그 창 줄은 onScreenLogs 로도 같이 간다(글은 웹이 짓는다) */
  onScenarioEvents?: (events: ScenarioLogEvent[]) => void;
  onMinorEdits?: (data: unknown[]) => void;
  onSimStates?: (states: SimStateSnapshot[]) => void;
  onTelemetries?: (telemetry: TelemetrySnapshot[]) => void;
  onError?: (error: Error) => void;
  /**
   * SimState → 화면 로그 줄 합성(콘솔 없는 종류의 진행 라인). **라우팅 시점**에
   * 불려 로그 버퍼에 SCREENLOG 줄과 봉투 도착 순서 그대로 섞인다 — flush 시점에
   * 합성하면 버퍼(종류)별로 블록이 갈라져, 백필처럼 한 번에 반영하는 경로에서
   * 두 블록의 시간 순서가 통째로 뒤집힌다. null 반환 = 이 상태는 줄 없음.
   */
  simStateToLogLine?: (state: SimStateSnapshot) => string | null;
}

export interface TaskRoute {
  callbacks: PollingCallbacks;
  screenLogBuffer: string[];
  scenarioEventBuffer: ScenarioLogEvent[];
  simStateBuffer: SimStateSnapshot[];
  telemetryBuffer: TelemetrySnapshot[];
  minorEditBuffer: unknown[];
}

/**
 * 라이브 라우팅을 백필이 끝날 때까지 **막아 두는** 문.
 *
 * 파이프라인은 구독을 먼저 열고 백필을 나중에 한다(구독은 "지금부터"라, 백필을
 * 먼저 하면 그 사이 프레임이 유실된다). 그래서 백필 내용은 이미 들어와 있는
 * 라이브보다 과거일 수 있고, 그대로 이으면 순서가 뒤집힌다.
 *
 * 실측(2026-08-10 로컬)으로는 백필 3.8~4.0초 / 첫 라이브 프레임 13.8초라 백필이
 * 먼저 도착해 순서가 우연히 맞는다. 그 **시간 관계에 기대지 않으려고** 문을 둔다.
 *
 * 닫힌 동안에는 봉투를 담아만 둔다 — 디코드·dedup 등록·콜백이 전부 일어나지
 * 않으므로, 라이브가 백필보다 먼저 스토어에 닿을 경로 자체가 없다. 방류하면
 * 도착 순서대로 흘려보내고, 백필과 겹치는 프레임은 dedup 이 걷어낸다.
 */
export interface FrameGate {
  open: boolean;
  held: SimStreamEnvelope[];
  /** 붙잡은 시간(ms) 계측용 — 열릴 때 로그로 찍는다 */
  closedAt: number;
  /** 방류 함수 — 라우트·dedup 을 쥔 startSimStreams 가 심어 준다. */
  release?: () => GateReleaseStat;
  /**
   * 구독의 수명 신호 — startSimStreams 가 심어 준다. 백필이 이 신호를 물면
   * 어떤 해체 경로든(stopAllStreams·같은 키 교체·stopAll) stop() 의 abort 가
   * 요청을 끊어, 게이트 방류(호출자의 finally)가 보장되고 늦은 응답이 다음
   * 세션의 스토어에 닿지 않는다.
   */
  abortSignal?: AbortSignal;
}

export interface GateReleaseStat {
  /** 붙잡고 있던 봉투 수 — 0 이면 백필이 라이브보다 먼저 도착했다는 뜻 */
  count: number;
  heldMs: number;
}

export function createFrameGate(): FrameGate {
  return { open: false, held: [], closedAt: Date.now() };
}

/**
 * 문을 열고 담아 둔 봉투를 **도착 순서대로** 흘려보낸다.
 * 백필이 이미 dedup 을 채웠으므로 겹치는 프레임은 여기서 걷힌다.
 * 두 번 불러도 안전하다(방류는 한 번뿐).
 */
export function releaseFrameGate(
  gate: FrameGate,
  routes: Map<string, TaskRoute>,
  dedup?: FrameDedup,
): GateReleaseStat {
  if (gate.open) return { count: 0, heldMs: 0 };
  gate.open = true;
  const held = gate.held.splice(0, gate.held.length);
  for (const envelope of held) routeEnvelope(envelope, routes, dedup);
  return { count: held.length, heldMs: Date.now() - gate.closedAt };
}

/** 스트림이 끊길 때 — 방류하지 않고 버린다(스토어에 쓸 곳이 이미 없다). */
export function discardFrameGate(gate: FrameGate): void {
  gate.open = true;
  gate.held.length = 0;
}

/** 새 라우트의 빈 버퍼 묶음 — 버퍼 종류가 늘어도 생성처가 흩어지지 않게. */
export function createTaskRoute(callbacks: PollingCallbacks): TaskRoute {
  return {
    callbacks,
    screenLogBuffer: [],
    scenarioEventBuffer: [],
    simStateBuffer: [],
    telemetryBuffer: [],
    minorEditBuffer: [],
  };
}

/** 모아 둔 프레임을 콜백으로 넘기고 버퍼를 비운다(비어 있으면 호출 없음). */
export function flushRoute(route: TaskRoute): void {
  const { callbacks } = route;
  const drain = <T,>(buffer: T[], deliver?: (batch: T[]) => void) => {
    if (buffer.length === 0) return;
    const batch = buffer.splice(0, buffer.length);
    deliver?.(batch);
  };
  drain(route.screenLogBuffer, callbacks.onScreenLogs);
  drain(route.scenarioEventBuffer, callbacks.onScenarioEvents);
  drain(route.simStateBuffer, callbacks.onSimStates);
  drain(route.telemetryBuffer, callbacks.onTelemetries);
  drain(route.minorEditBuffer, callbacks.onMinorEdits);
}

function mapSimState(message: SimState): SimStateSnapshot {
  return {
    task_id: message.taskId,
    task_type: message.taskType,
    seq: Number(message.seq),
    ts_ms: Number(message.tsMs),
    sim_time_ms: Number(message.simTimeMs),
    status: message.status,
    iteration: Number(message.iteration),
    target_speed: Number(message.targetSpeed),
    actual_speed: Number(message.actualSpeed),
    max_speed: Number(message.maxSpeed),
  };
}

/** 백필(GetTelemetryRange)도 같은 매핑을 쓴다 — 라이브·백필 프레임 동형 보장 */
export function mapTelemetry(message: Telemetry): TelemetrySnapshot {
  return {
    task_id: message.taskId,
    task_type: message.taskType,
    seq: Number(message.seq),
    ts_ms: Number(message.tsMs),
    sim_time_ms: Number(message.simTimeMs),
    groups: (message.groups || []).map((group) => ({
      kind: group.kind,
      objectId: Number(group.objectId),
      samples: (group.samples || []).map((s) => ({
        name: s.name,
        value: s.value,
        unit: s.unit,
      })),
    })),
  };
}

/** 봉투 하나를 디코드해 해당 태스크의 콜백으로 분배한다. */

/**
 * [correctness] 백필과 라이브를 **병행**으로 받을 때의 중복 제거.
 *
 * 구독은 "지금부터"다 — BFF 가 구독 시점의 마지막 스트림 ID 를 커서로 고정한다
 * (`redis_stream_consumer._resolve_start_id`). 백필을 먼저 하고 구독을 나중에 하면
 * 그 사이 프레임이 **유실**된다(2026-08-10 실측 6~10초). 순서를 뒤집으면 유실은
 * 사라지지만 겹치는 구간이 생기고, 라이브 append 경로에는 중복 제거가 없어
 * 차트 점·로그 줄이 그대로 겹쳐 보인다.
 *
 * 키: `kind:task_id:seq` — SimState·Telemetry·MinorEdit 은 태스크 단위, 화면 로그는 `kind:task_id:channel:seq` — 채널마다
 * 발행자가 따로라 (task_id, channel) 단위로 1 부터 단조 증가한다(adapter-core 계약 §6 «seq 의 단위», `frameStream`).
 * redis 원본 프레임으로 실제 값 확인함(seq=1,2,3…).
 *
 * 겹침은 **초기 병합 구간에만** 생기므로 그동안만 유지하고 버린다.
 */
export interface FrameDedup {
  seen: Set<string>;
  active: boolean;
  /**
   * 백필이 실어 온 스트림(`frameStream` — `kind:taskId`, 화면 로그는 `kind:taskId:channel`)별 **마지막 seq** — 겹침이 끝나는 지점.
   * 라이브가 이 값을 넘어서면 그 스트림의 중복은 더 나올 수 없다.
   */
  overlapEnd: Map<string, number>;
  /** 백필 적용이 끝나 겹침 감시가 시작됐는가 */
  armed: boolean;
}

export function createFrameDedup(): FrameDedup {
  return { seen: new Set<string>(), active: true, overlapEnd: new Map(), armed: false };
}

/**
 * 백필 적용이 끝난 시점에 부른다 — 여기까지 등록된 것이 겹침 구간이다.
 *
 * 종전에는 "5초쯤이면 겹침이 끝났겠지"라며 setTimeout 으로 껐다. 그건 짐작이지
 * 동기화가 아니다. 백필이 각 스트림의 마지막 seq 를 알고 있으므로, 라이브가
 * 그 지점을 지나간 것을 **관측해서** 끈다. 느리든 빠르든 정확히 맞는다.
 *
 * 게이트가 백필보다 앞선 라이브를 막아 주므로, arm 이전에 등록된 프레임은
 * 전부 백필분이다.
 */
export function armFrameDedup(dedup: FrameDedup): void {
  dedup.armed = true;
  // 겹칠 것이 없으면 감시할 것도 없다
  if (dedup.overlapEnd.size === 0) stopFrameDedup(dedup);
}

/** 겹침 구간이 끝났다 — 이후 라이브는 단일 출처라 중복이 없다 */
export function stopFrameDedup(dedup: FrameDedup): void {
  dedup.active = false;
  dedup.seen.clear();
  dedup.overlapEnd.clear();
}

/**
 * 프레임 스트림의 정체 — seq 가 단조 증가하는 단위이자 중복을 거르는 단위. SimState·Telemetry·MinorEdit 는 태스크마다
 * 발행자가 하나라 (종류, 태스크)다. 화면 로그는 **채널마다** 발행자가 따로라(콘솔 채널 = 잡 래퍼, `scenario` = 서버 —
 * adapter-core 계약 `sim-stream-contract-design.md` §6 «seq 의 단위») (종류, 태스크, 채널)이다. 채널을 빼면 두 발행자의
 * seq 가 겹쳐 서로의 줄을 «중복» 으로 지운다.
 */
function frameStream(kind: number, taskId: string, channel?: string): string {
  return channel === undefined ? `${kind}:${taskId}` : `${kind}:${taskId}:${channel}`;
}

function isDuplicateFrame(
  dedup: FrameDedup | undefined,
  streamKey: string,
  seq: number | bigint,
): boolean {
  if (!dedup?.active) return false;
  const seqNum = Number(seq);

  if (!dedup.armed) {
    // 백필 적용 중 — 겹침의 끝을 기록해 둔다
    const prev = dedup.overlapEnd.get(streamKey);
    if (prev === undefined || seqNum > prev) dedup.overlapEnd.set(streamKey, seqNum);
  } else {
    const end = dedup.overlapEnd.get(streamKey);
    // 경계가 없는 스트림(백필에 없었거나 이미 지나감)의 프레임은 겹칠 수 없다
    // — seen 에 쌓지 않는다. 정지된 **다른** 스트림의 경계가 안 지워져 감시가
    // 세션 내내 남아도, 활성 스트림의 seen 누적은 여기서 끊긴다.
    if (end === undefined) return false;
    // 라이브가 겹침 구간을 지나갔으면 그 스트림은 더 볼 필요가 없다
    if (seqNum > end) {
      dedup.overlapEnd.delete(streamKey);
      if (dedup.overlapEnd.size === 0) stopFrameDedup(dedup);
      return false;
    }
  }

  const key = `${streamKey}:${seqNum}`;
  if (dedup.seen.has(key)) return true;
  dedup.seen.add(key);
  return false;
}

export function routeEnvelope(
  envelope: SimStreamEnvelope,
  routes: Map<string, TaskRoute>,
  dedup?: FrameDedup,
  gate?: FrameGate,
): void {
  // 문이 닫혀 있으면 **아무것도 하지 않고** 담아만 둔다. 디코드도 dedup 등록도
  // 하지 않으므로, 라이브가 백필보다 먼저 스토어에 닿을 경로가 없다.
  if (gate && !gate.open) {
    gate.held.push(envelope);
    return;
  }

  switch (envelope.kind) {
    case SimStreamKind.SIMSTATE: {
      const message = SimState.fromBinary(envelope.payload);
      if (isDuplicateFrame(dedup, frameStream(envelope.kind, message.taskId), message.seq)) return;
      const route = routes.get(message.taskId);
      if (!route) return;
      const snapshot = mapSimState(message);
      if (route.callbacks.onSimStates) route.simStateBuffer.push(snapshot);
      // 진행 라인은 여기서(도착 순서 위치에서) 로그 버퍼에 넣는다 — 주석은
      // simStateToLogLine 선언부 참조.
      if (route.callbacks.simStateToLogLine && route.callbacks.onScreenLogs) {
        const line = route.callbacks.simStateToLogLine(snapshot);
        if (line !== null) route.screenLogBuffer.push(line);
      }
      return;
    }
    case SimStreamKind.SCREENLOG: {
      const message = ScreenLogLine.fromBinary(envelope.payload);
      if (isDuplicateFrame(dedup, frameStream(envelope.kind, message.taskId, message.channel), message.seq)) return;
      const route = routes.get(message.taskId);
      if (!route) return;
      const { callbacks } = route;
      if (message.channel === SCENARIO_LOG_CHANNEL) {
        // 시나리오 사건 — 종류의 채널 필터와 무관. 구조를 읽어 글은 로그 창으로, 사건은 차트 표식으로.
        // 계약에 맞지 않는 줄(옛 어댑터의 글)은 원문 그대로 로그 창에만
        const event = parseScenarioLogEvent(message.line);
        if (event) {
          route.screenLogBuffer.push(scenarioLogEventText(event));
          route.scenarioEventBuffer.push(event);
        } else if (typeof message.line === 'string') {
          route.screenLogBuffer.push(message.line);
        }
        return;
      }
      if (callbacks.screenLogChannels && !callbacks.screenLogChannels.includes(message.channel)) {
        return;
      }
      if (callbacks.onScreenLogs && typeof message.line === 'string') {
        route.screenLogBuffer.push(message.line);
      }
      return;
    }
    case SimStreamKind.TELEMETRY: {
      const message = Telemetry.fromBinary(envelope.payload);
      if (isDuplicateFrame(dedup, frameStream(envelope.kind, message.taskId), message.seq)) return;
      const route = routes.get(message.taskId);
      if (route?.callbacks.onTelemetries) route.telemetryBuffer.push(mapTelemetry(message));
      return;
    }
    case SimStreamKind.MINOREDIT: {
      const message = MarsMinorEditSnapshot.fromBinary(envelope.payload);
      if (isDuplicateFrame(dedup, frameStream(envelope.kind, message.taskId), message.seq)) return;
      const route = routes.get(message.taskId);
      if (!route?.callbacks.onMinorEdits) return;
      route.minorEditBuffer.push({
        ts_ms: Number(message.tsMs),
        timehy: Number(message.timehy),
        task_id: message.taskId,
        seq: Number(message.seq),
        values: (message.values || []).map((item) => ({
          name: item.name,
          value: item.value,
        })),
      });
      return;
    }
    default:
      console.warn('[SimStreamRouter] Unknown stream kind:', envelope.kind);
  }
}
